import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, MapPin, Award } from "lucide-react";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "forestry", label: "Forestry" },
  { key: "renewable", label: "Renewable" },
  { key: "ocean", label: "Ocean" },
  { key: "agriculture", label: "Agriculture" },
  { key: "direct_air", label: "Direct Air" },
];

export default function Marketplace() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const { format, currency, lastUpdated } = useCurrency();

  useEffect(() => {
    setLoading(true);
    api.get("/projects", { params: { category, search: search || undefined } })
      .then((r) => setProjects(r.data))
      .finally(() => setLoading(false));
  }, [category, search]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Marketplace</div>
          <h1 className="text-4xl sm:text-5xl tracking-tighter font-light mt-2">Choose your impact</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">Every project is independently verified. Filter by category, region, or price. Companies from any country can buy — pay in your local currency or crypto.</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400"/>
          <Input
            data-testid="marketplace-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search projects…"
            className="pl-10 bg-stone-50 border-stone-200 rounded-full h-11"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`filter-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`px-4 py-2 rounded-full text-sm transition-colors border ${
              category === c.key
                ? "bg-[#2C5530] text-white border-[#2C5530]"
                : "bg-white border-stone-200 text-stone-700 hover:border-[#2C5530]"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-stone-500">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="text-stone-500">No projects found.</div>
      ) : (
        <>
          {lastUpdated && (
            <div className="text-xs text-stone-500 mb-4">
              Prices in {currency.toUpperCase()} · live FX updated {new Date(lastUpdated).toLocaleString()}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/project/${p.id}`}
              data-testid={`project-card-${p.id}`}
              className="group border border-stone-200 rounded-2xl overflow-hidden bg-white hover:-translate-y-1 hover:shadow-lg transition-transform duration-300"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-[#2C5530]/10 text-[#2C5530] hover:bg-[#2C5530]/10 border-0 capitalize">{p.category.replace("_"," ")}</Badge>
                  <span className="text-xs text-stone-500 flex items-center gap-1"><Award className="w-3 h-3"/>{p.certification}</span>
                </div>
                <h3 className="text-lg font-medium tracking-tight leading-snug">{p.name}</h3>
                <div className="flex items-center gap-1 text-sm text-stone-500 mt-1">
                  <MapPin className="w-3.5 h-3.5"/> {p.location}
                </div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-2xl font-light tracking-tight">{format(p.price_per_ton)}</div>
                    <div className="text-xs text-stone-500">per ton CO₂ · {currency.toUpperCase()}</div>
                  </div>
                  <Button size="sm" className="bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">View →</Button>
                </div>
              </div>
            </Link>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
